import { cloneDeep, omit } from 'lodash';
import { Constants, USER_ROLES } from '@pwc/pwc-deals-web-common';
import { isPowerPivotLibraryItem } from 'features/analyses/common/LibraryItemCard/PowerPivot/constants/PowerPivotTypes.ts';
import { isDisplayFormatEnabled } from 'common/helpers/featureFlags';
import {
  getExcelLibraryItemsByGroup,
  getPptLibraryItemsByGroup,
  getRichTextLibraryItemsByGroup,
} from '../contentLibrary/libraryItemService';
import {
  getAnalysis,
  getAnalysisVersions,
  getAnalysisDetailsStatus,
  getAnalysisDetailsShared,
  getAnalysisDetailsDraft,
  getAnalysisDetailsVersions,
  getAnalysisVersionsPrevious,
} from './analysesService';
import { getPowerBiReportData } from './powerBiService';
import {
  fetchAnalysisBegan,
  fetchAnalysisSucceeded,
  fetchAnalysisFailed,
  fetchAnalysisDetailBegin,
  fetchAnalysisDetailEnd,
  fetchAnalysisContentStart,
  fetchAnalysisContentEnd,
  fetchAnalysisContent,
  fetchAnalysisContentError,
  fetchAnalysisContentErrorStopLoading,
  fetchAnalysisLibraryItemGroups,
  fetchAnalysisDetailFailed,
  fetchAnalysisVersionsSuccess,
  fetchLibraryItemsNew,
  updateSectionItems,
  updateSectionItemsFailed,
  updateSections,
  updateSharedStatus,
  updateVersions,
} from './analysisSlice';
import { getStructuredAnalysis } from '../../common/engine/services/structuredAnalysisService';
import { STRUCTURED_DISPLAY_NAME_BY_SUBTYPE } from '../../common/engine/utilities/constants';
import { getTableauReportDetail } from './tableauService.ts';
import { TABLEAU_LIBRARY_ITEM_TYPES } from '../../common/components/SectionArea/libraryItemUtils.ts';

const formatTypeMap = {
  300: 'SVG',
  301: 'SVG', // default format if item has not formatType
  302: 'SVG', // default format if item has not formatType
  500: 'SVG',
};

const libraryItemTypeApiMethodMap = {
  300: getExcelLibraryItemsByGroup,
  301: getExcelLibraryItemsByGroup,
  302: getExcelLibraryItemsByGroup,
  500: getPptLibraryItemsByGroup,
  400: getPowerBiReportData,
  401: getPowerBiReportData,
  700: getTableauReportDetail,
  701: getTableauReportDetail,
};

const requestArrayType = {
  500: 'pptLibraryItems',
  301: 'excelLibraryItems',
  302: 'excelLibraryItems',
  300: 'excelLibraryItems',
};

const updateLibraryItemsThunk = (itemsToAdd) => async (dispatch, getState) => {
  const {
    analysis: {
      libraryItemsNew: { items },
    },
  } = getState();
  const newItems = cloneDeep(items).concat(itemsToAdd);
  dispatch(fetchLibraryItemsNew({ libraryItems: newItems }));
  dispatch(updateSectionItems());
};

const transformPowerBiResponse = (response, data) => {
  const { libraryItemId, libraryItemType } = response;
  const clonedResponse = cloneDeep(
    omit(response, ['libraryItemId', 'libraryItemType'])
  );
  const transformedResponse = {
    ...clonedResponse,
    id: libraryItemId,
    type: libraryItemType,
    data,
  };
  return transformedResponse;
};

const transformTableauResponse = (response, data) => {
  const {
    libraryItemId,
    libraryItemType,
    libraryItemName,
    libraryItemReportId,
  } = response;
  const clonedResponse = cloneDeep(
    omit(response, ['libraryItemId', 'libraryItemType'])
  );
  const transformedResponse = {
    ...clonedResponse,
    id: libraryItemId,
    type: libraryItemType,
    name: libraryItemName,
    reportId: libraryItemReportId,
    data,
  };
  return transformedResponse;
};

const fetchPowerBiSingleItemThunk =
  (projectId, reqData, libItem) => async (dispatch) => {
    try {
      const { data } = reqData;
      const response = await libraryItemTypeApiMethodMap[libItem.type](
        projectId,
        reqData
      );
      const transformedResponse = transformPowerBiResponse(response, data);
      dispatch(updateLibraryItemsThunk(transformedResponse));
    } catch (error) {
      const { libraryItemId, libraryItemType } = reqData;
      dispatch(
        updateSectionItemsFailed({
          allIds: [libraryItemId],
          type: libraryItemType,
        })
      );
    }
  };

const fetchTableauSingleItemThunk =
  (projectId, reqData, libItem) => async (dispatch) => {
    try {
      const { data, reportId, workspaceId } = reqData;
      const response = await libraryItemTypeApiMethodMap[libItem.type](
        projectId,
        workspaceId,
        reportId
      );

      const transformedResponse = transformTableauResponse(
        {
          libraryItemId: libItem.id,
          libraryItemType: libItem.type,
          libraryItemName: libItem.name,
          libraryItemReportId: reportId,
          ...response,
        },
        data
      );
      dispatch(updateLibraryItemsThunk(transformedResponse));
    } catch (error) {
      const { libraryItemId, libraryItemType } = reqData;
      dispatch(
        updateSectionItemsFailed({
          allIds: [libraryItemId],
          type: libraryItemType,
        })
      );
    }
  };

export const loadPowerBiLibraryItemsThunk =
  (projectId) => async (dispatch, getState) => {
    const state = getState();
    const { powerBiLibraryItemList } = state.analysis.libraryItemGroups || {};

    if (!powerBiLibraryItemList || powerBiLibraryItemList?.length === 0) {
      return;
    }

    powerBiLibraryItemList.forEach((group) => {
      const reqBody = {
        reportId: group.reportId,
        embedUrl: group.embedUrl,
      };
      group.powerBiLibraryItems.forEach((libItem) => {
        const reqData = {
          ...reqBody,
          libraryItemId: libItem.id,
          libraryItemType: libItem.type,
          workspaceId: libItem.workspaceId,
          name: libItem.name,
          data: libItem.data,
        };

        dispatch(fetchPowerBiSingleItemThunk(projectId, reqData, libItem));
      });
    });
  };

export const loadTableauLibraryItemsThunk =
  (projectId, workspaceId) => async (dispatch, getState) => {
    if (!workspaceId) {
      return;
    }

    const state = getState();
    const { tableauLibraryItemList } = state.analysis.libraryItemGroups || {};

    if (!tableauLibraryItemList || tableauLibraryItemList?.length === 0) {
      return;
    }

    tableauLibraryItemList.forEach((group) => {
      const reqBody = {
        reportId: group.reportId,
        embedUrl: group.embedUrl,
      };
      group.tableauLibraryItems.forEach((libItem) => {
        const reqData = {
          ...reqBody,
          libraryItemId: libItem.id,
          libraryItemType: libItem.type,
          workspaceId,
          name: libItem.name,
          data: libItem.data,
        };

        dispatch(fetchTableauSingleItemThunk(projectId, reqData, libItem));
      });
    });
  };

const updatePowerPivotThunk = (item) => async (dispatch) => {
  try {
    dispatch(updateLibraryItemsThunk(item));
  } catch (error) {
    const { libraryItemId, libraryItemType } = item;
    dispatch(
      updateSectionItemsFailed({
        allIds: [libraryItemId],
        type: libraryItemType,
      })
    );
  }
};

export const fetchPowerPivotLibraryItemsThunk =
  () => async (dispatch, getState) => {
    const state = getState();
    const { dynamicExcelLibraryItemList } =
      state.analysis.libraryItemGroups || {};

    if (
      !dynamicExcelLibraryItemList ||
      dynamicExcelLibraryItemList?.length === 0
    ) {
      return;
    }

    dynamicExcelLibraryItemList.forEach((group) => {
      const dynamicExcelLibraryItem = {
        groupedByKey: group.groupedByKey,
        sharePointItemUrl: group.sharePointItemUrl,
        siteId: group.siteId,
        driveId: group.driveId,
        driveItemId: group.driveItemId,
      };
      group.dynamicExcelLibraryItems.forEach((powerPivot) => {
        const item = {
          ...dynamicExcelLibraryItem,
          ...powerPivot,
          libraryItemId: powerPivot.id,
          libraryItemType: powerPivot.type,
          libraryItemName: powerPivot.data.name,
        };
        dispatch(updatePowerPivotThunk(item));
      });
    });
  };

const transformExcelPptGroupResponse = (
  response,
  excelLibraryItems,
  pptLibraryItems
) => {
  // fixing Bug 29577: Error message is absent if content is not available - return empty object, do not fail
  if (response === null) return [];
  const { fileName, fileContents } = response;

  return fileContents.map((item, id) => {
    const { data = {} } = item.autoSync || {};
    const cloned = cloneDeep(item);
    return {
      ...cloned,
      fileName,
      worksheetName: item.worksheetName || data.worksheetName,
      svgScaleFactor:
        (excelLibraryItems && excelLibraryItems[id]?.data?.svgScaleFactor) ||
        (pptLibraryItems && pptLibraryItems[id]?.data?.svgScaleFactor),
    };
  });
};

const fetchExcelPptSingleGroupThunk =
  (projectId, reqBody, groupType, allIds) => async (dispatch) => {
    try {
      dispatch(fetchAnalysisContentStart());

      const response = await libraryItemTypeApiMethodMap[groupType](
        projectId,
        reqBody
      );
      const { excelLibraryItems, pptLibraryItems } = reqBody;

      const transformedItems = transformExcelPptGroupResponse(
        response,
        excelLibraryItems,
        pptLibraryItems
      );
      const notLoadedItems = allIds.filter(
        (x) => !transformedItems.map((m) => m.id).includes(x)
      );
      dispatch(
        updateLibraryItemsThunk(
          transformedItems.concat(
            notLoadedItems.map((id) => ({ id, type: -1 }))
          )
        )
      );
      dispatch(fetchAnalysisContentEnd());
    } catch (err) {
      dispatch(fetchAnalysisContentErrorStopLoading());

      const getDataByIdError = (theErr) => ({
        error: {
          ...theErr,
          data: {
            sharePointItemUrl: reqBody.sharePointItemUrl,
          },
          type: groupType,
        },
        type: groupType,
      });

      // example err: {
      //   apiErrorCode: "E1005-104"
      //   apiErrorMessage: "Could not find Excel drive item"
      //   errorCode: "E1005-102-104"
      // }

      if (allIds.length > 1) {
        // the error occurs for one single item in the allIds group, which means that we do not know which one did not error
        // so multiple fetches are needed
        const allIdsDataById = {};
        const items = reqBody[requestArrayType[groupType]];

        const fetchesItems = items.map((item) => {
          const reqBodyItem = {
            ...reqBody,
          };

          reqBodyItem[requestArrayType[groupType]] = [item];

          return libraryItemTypeApiMethodMap[groupType](
            projectId,
            reqBodyItem
          ).catch((theErr) => {
            allIdsDataById[item.id] = getDataByIdError(theErr);
          });
        });

        await Promise.all(fetchesItems);

        dispatch(
          updateSectionItemsFailed({
            allIds,
            allIdsDataById,
          })
        );
      } else {
        dispatch(
          updateSectionItemsFailed({
            allIds,
            allIdsDataById: allIds[0]
              ? {
                  [allIds[0]]: getDataByIdError(err),
                }
              : undefined,
          })
        );
      }
    }
  };

export const loadExcelPptLibraryItemsItemsThunk =
  (projectId) => async (dispatch, getState) => {
    const state = getState();
    const { excelPptLibraryItemList } = state.analysis.libraryItemGroups || {};

    if (!excelPptLibraryItemList || excelPptLibraryItemList?.length === 0) {
      return;
    }

    excelPptLibraryItemList.forEach((group) => {
      const reqBody = {
        groupedByKey: group.groupedByKey,
        sharePointItemUrl: group.sharePointItemUrl,
        driveId: group.driveId,
        driveItemId: group.driveItemId,
        siteId: group.siteId,
      };
      const groupType = group.excelPptLibraryItems[0].type;
      const allIds = [];
      reqBody[requestArrayType[groupType]] = group.excelPptLibraryItems.map(
        (libItem) => {
          allIds.push(libItem.id);
          const cloned = JSON.parse(JSON.stringify(libItem));
          const { data } = cloned;
          data.formatType = formatTypeMap[libItem.type];

          if (
            isDisplayFormatEnabled &&
            (libItem.type ===
              Constants.LIBRARY_ITEM_TYPES.EXCEL_CELL_RANGE.value ||
              libItem.type ===
                Constants.LIBRARY_ITEM_TYPES.EXCEL_NAMED_RANGE.value)
          ) {
            data.formatType =
              libItem?.data?.formatType || formatTypeMap[libItem.type];
          }

          if (
            libItem.type ===
            Constants.LIBRARY_ITEM_TYPES.EXCEL_NAMED_RANGE.value
          ) {
            data.namedRange = data.name;
          }

          if (
            libItem.type ===
              Constants.LIBRARY_ITEM_TYPES.EXCEL_CELL_RANGE.value ||
            libItem.type ===
              Constants.LIBRARY_ITEM_TYPES.EXCEL_NAMED_RANGE.value
          ) {
            // eslint-disable-next-line prefer-destructuring
            if (!data.worksheetName) {
              data.worksheetName = data.name.split('!')[0].replaceAll("'", '');
            }
            data.cellRange = data.name;
            delete data.name;
            delete cloned.childrenData;
          }

          if (
            libItem.type ===
            Constants.LIBRARY_ITEM_TYPES.EXCEL_NAMED_RANGE.value
          ) {
            data.cellRange = libItem.data?.cellRange;
          }
          return { ...cloned, data };
        }
      );
      dispatch(
        fetchExcelPptSingleGroupThunk(projectId, reqBody, groupType, allIds)
      );
    });
  };

export const fetchRichTextGroupedItemsThunk =
  (projectId, contextStatusInfoItem) => async (dispatch, getState) => {
    const state = getState();

    const isPwcUser = state.loggedInUser.userRole === USER_ROLES.PWC_USER;

    const { richTextLibraryItemList } = state.analysis.libraryItemGroups || {};

    if (!richTextLibraryItemList || richTextLibraryItemList?.length === 0) {
      return;
    }

    try {
      const richTextLibItems = richTextLibraryItemList
        .reduce((accum, item) => {
          const { libraryItemId: itemId } = item;
          const parsedId = Number.isInteger(parseInt(itemId, 10))
            ? parseInt(itemId, 10)
            : null;
          return accum.concat([parsedId]);
        }, [])
        .filter((item) => item !== null);
      const reqBody = {
        libraryItemIds: richTextLibItems,
      };
      const response = await getRichTextLibraryItemsByGroup(
        projectId,
        reqBody,
        contextStatusInfoItem,
        isPwcUser
      );
      const { richTextLibraryItems } = response;
      dispatch(updateLibraryItemsThunk(richTextLibraryItems));
    } catch (e) {
      const allIds = richTextLibraryItemList.map((l) =>
        Number(l.libraryItemId)
      );
      dispatch(updateSectionItemsFailed({ allIds, type: 100 }));
    }
  };

const transformSharedItem = (existingSectionItem, sharedItem) => {
  const { type } = sharedItem;
  let newSectionItem;
  if (type === Constants.LIBRARY_ITEM_TYPES.RICH_TEXT.value) {
    const { data } = sharedItem;
    newSectionItem = {
      ...existingSectionItem,
      ...omit(sharedItem, ['data']),
      richTextContent: { richTextData: data },
    };
  } else if (
    type === Constants.LIBRARY_ITEM_TYPES.POWER_BI_REPORT.value ||
    type === Constants.LIBRARY_ITEM_TYPES.POWER_BI_REPORT_PAGE.value ||
    type === TABLEAU_LIBRARY_ITEM_TYPES.TableauReport ||
    type === TABLEAU_LIBRARY_ITEM_TYPES.TableauReportPage ||
    isPowerPivotLibraryItem(type)
  ) {
    newSectionItem = { ...existingSectionItem, ...sharedItem };
  } else if (
    type === Constants.LIBRARY_ITEM_TYPES.EXCEL_CHART.value ||
    type === Constants.LIBRARY_ITEM_TYPES.EXCEL_CELL_RANGE.value ||
    type === Constants.LIBRARY_ITEM_TYPES.EXCEL_NAMED_RANGE.value ||
    type === Constants.LIBRARY_ITEM_TYPES.POWER_POINT_GROUP.value
  ) {
    const { data } = sharedItem;
    newSectionItem = {
      ...existingSectionItem,
      ...omit(sharedItem, ['data']),
      value: data,
    };
  } else {
    fetchAnalysisContentError();
  }
  return newSectionItem;
};

const updateSharedLibraryItemsThunk =
  (itemsToAdd) => async (dispatch, getState) => {
    const {
      analysis: {
        libraryItemsShared: { items: sharedItems },
        sections,
      },
    } = getState();
    const newItems = cloneDeep(sharedItems).concat(itemsToAdd);
    const newSections = sections.map((section) => {
      const { items } = section;
      const transformedItems = items.map((item) => {
        const clonedItem = cloneDeep(item);
        const updatedItem = newItems.find(
          (_) => _.id === (item.id || item.libraryItemId)
        );
        let transformedSharedItem;
        if (updatedItem) {
          transformedSharedItem = transformSharedItem(clonedItem, updatedItem);
          return {
            ...transformedSharedItem,
            isLoading: false,
            hasError: false,
            childrenData: newItems.filter((i) => i.type !== 100),
          };
        }
        return {
          ...clonedItem,
          // TODO: on client draft has deleted content stuck on loading
          isLoading: false,
          hasError: !newItems.some(
            (el) => el.id !== (item.id || item.libraryItemId)
          ),
        };
      });

      return {
        ...section,
        items: transformedItems,
      };
    });
    dispatch(updateSections({ newSections }));
  };

export const loadAnalysisVersionsThunk =
  (projectId, analysisId) => async (dispatch) => {
    try {
      const versions = await getAnalysisVersions(projectId, analysisId);
      dispatch(fetchAnalysisVersionsSuccess({ versions }));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

export const loadAnalysisWithDraftContent =
  (projectId, analysisId, workspaceId, contextStatusInfoItem) =>
  async (dispatch) => {
    try {
      dispatch(fetchAnalysisDetailBegin());

      const response = await getAnalysisDetailsDraft(projectId, analysisId);
      const { content = {}, libraryItemGroups = {} } = response;
      dispatch(fetchAnalysisContent({ content }));
      dispatch(fetchAnalysisLibraryItemGroups({ libraryItemGroups }));

      dispatch(loadExcelPptLibraryItemsItemsThunk(projectId));
      dispatch(loadPowerBiLibraryItemsThunk(projectId));
      dispatch(loadTableauLibraryItemsThunk(projectId, workspaceId));
      dispatch(
        fetchRichTextGroupedItemsThunk(projectId, contextStatusInfoItem)
      );
      dispatch(fetchPowerPivotLibraryItemsThunk());
    } catch (e) {
      // eslint-disable-next-line no-console
      dispatch(
        fetchAnalysisDetailFailed({
          isLoading: false,
          hasError: true,
          errorMessage: e.toString(),
        })
      );
    } finally {
      dispatch(fetchAnalysisDetailEnd());
    }
  };

const createLoadAnalysisWithSharedContentBase =
  ({ fetchFn, onError }) =>
  ({ analysisID, contextStatusInfoItem, isClientUser, projectID }) =>
  async (dispatch, getState) => {
    const state = getState();

    const isPwcUser = state.loggedInUser.userRole === USER_ROLES.PWC_USER;

    try {
      dispatch(fetchAnalysisContentStart());

      const { content, items, richTextResolution } = await fetchFn({
        analysisID,
        projectID,
      });

      if (!isClientUser) {
        // simulate something like fetchRichTextGroupedItemsThunk is done for draft
        // we need to call getRichTextLibraryItemsByGroup to fill in the placeholder library items
        const richTextLibraryItemIds = [];
        // eslint-disable-next-line no-restricted-syntax
        for (const item of items) {
          if (item.type === Constants.LIBRARY_ITEM_TYPES.RICH_TEXT.value) {
            richTextLibraryItemIds.push(item.id);
          }
        }
        if (richTextLibraryItemIds.length > 0) {
          await getRichTextLibraryItemsByGroup(
            projectID,
            {
              libraryItemIds: richTextLibraryItemIds,
            },
            contextStatusInfoItem,
            isPwcUser
          );
        }
      }

      dispatch(fetchAnalysisContent({ content, richTextResolution }));
      dispatch(updateSharedLibraryItemsThunk(items));
    } catch (err) {
      if (onError) {
        onError({
          dispatch,
          err,
        });
      } else {
        dispatch(fetchAnalysisContentError());
      }
    } finally {
      dispatch(fetchAnalysisContentEnd());
    }
  };

export const loadAnalysisWithSharedContent =
  createLoadAnalysisWithSharedContentBase({
    fetchFn: ({ analysisID, projectID }) =>
      getAnalysisDetailsShared(projectID, analysisID),
  });

export const loadAnalysisWithSharedContentPrevious =
  createLoadAnalysisWithSharedContentBase({
    fetchFn: ({ analysisID, projectID }) =>
      getAnalysisVersionsPrevious(projectID, analysisID),
    onError: ({ dispatch }) =>
      dispatch(fetchAnalysisContentError({ errorType: 'previousVersion' })),
  });

export const loadAnalysisStatus =
  (projectId, analysisId) => async (dispatch) => {
    try {
      const statusResponse = await getAnalysisDetailsStatus(
        projectId,
        analysisId
      );
      dispatch(updateSharedStatus(statusResponse));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

export const loadAnalysisVersions =
  (
    projectId,
    analysisId
    // eslint-disable-next-line no-unused-vars
  ) =>
  async (dispatch) => {
    try {
      const versionsResponse = await getAnalysisDetailsVersions(
        projectId,
        analysisId
      );
      dispatch(updateVersions(versionsResponse));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

// Thunks
const loadThunk = async (dispatch, state, loadFunction) => {
  try {
    if (!state.isLoading) {
      dispatch(fetchAnalysisBegan());

      const response = await loadFunction();

      dispatch(fetchAnalysisSucceeded(response));
    }
  } catch (error) {
    dispatch(fetchAnalysisFailed(error.response ? error.response.status : 500));
  }
};

export const loadAnalysisThunk =
  (projectId, analysisId, versionStatus) => async (dispatch, state) => {
    await loadThunk(dispatch, state, () =>
      getAnalysis(projectId, analysisId, versionStatus)
    );
  };

export const loadStructuredAnalysisThunk =
  (projectId, analysisId, versionStatus) => async (dispatch, state) => {
    await loadThunk(dispatch, state, async () => {
      const analysis = await getStructuredAnalysis(
        projectId,
        analysisId,
        versionStatus
      );
      analysis.title =
        STRUCTURED_DISPLAY_NAME_BY_SUBTYPE[analysis.contentSubType];
      return analysis;
    });
  };
